import React from 'react'
import { cn } from '@/lib/utils'

export interface TextProps {
  /** The content to display */
  children: React.ReactNode
  /** The size variant of the text */
  size?: 'caption' | 'small' | 'body-small' | 'body' | 'body-large' | 'heading-small' | 'heading' | 'heading-large' | 'display-small' | 'display'
  /** The line height variant */
  lineHeight?: 'tight' | 'default' | 'relaxed'
  /** The color variant */
  color?: 'primary' | 'subdued' | 'muted'
  /** Text alignment */
  align?: 'left' | 'center' | 'right' | 'justified'
  /** Bottom margin spacing */
  spacing?: 'none' | 'tight' | 'default' | 'loose'
  /** The HTML element to render */
  as?: 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label'
  /** Additional CSS classes */
  className?: string
  /** Optional id for accessibility */
  id?: string
  /** Optional data-id for testing */
  'data-id'?: string
  /** For label elements - associates with form input */
  htmlFor?: string
}

// Color classes using Tailwind tokens
const colorClasses = {
  primary: 'text-foreground',
  subdued: 'text-muted-foreground',
  muted: 'text-muted-foreground',
}

export const Text = ({
  children,
  size = 'body',
  lineHeight = 'default',
  color = 'primary',
  align = 'left',
  spacing = 'none',
  as: Component = 'p',
  className = '',
  id,
  'data-id': dataId,
  htmlFor,
}: TextProps) => {
  // Size classes
  const sizeClasses = {
    caption: 'text-[11px]',
    small: 'text-[12px]',
    'body-small': 'text-[13px]',
    body: 'text-[14px]',
    'body-large': 'text-[16px]',
    'heading-small': 'text-[18px] font-[650] tracking-[-0.2px]',
    heading: 'text-[20px] font-[650] tracking-[-0.2px]',
    'heading-large': 'text-[22px] font-[650] tracking-[-0.2px]',
    'display-small': 'text-[24px] font-[650] tracking-[-0.2px]',
    display: 'text-[30px] font-[650] tracking-[-0.2px]',
  }

  // Line height classes
  const lineHeightClasses = {
    tight: 'leading-[1.3]',
    default: 'leading-[1.5]',
    relaxed: 'leading-[1.7]',
  }

  // Alignment classes
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justified: 'text-justify',
  }

  // Spacing classes
  const spacingClasses = {
    none: '',
    tight: 'mb-2',
    default: 'mb-4',
    loose: 'mb-8',
  }

  // Apply font-medium automatically for label elements
  const labelClasses = Component === 'label' ? 'font-medium' : ''

  return (
    <Component
      className={cn(
        sizeClasses[size],
        lineHeightClasses[lineHeight],
        colorClasses[color],
        alignClasses[align],
        spacingClasses[spacing],
        labelClasses,
        className
      )}
      id={id}
      data-id={dataId}
      {...(Component === 'label' && htmlFor ? { htmlFor } : {})}
    >
      {children}
    </Component>
  )
}
